import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Chip
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import './SessionReporting.css';
import { getUniqueValues, calculateSummaryStats, transformSessionDataFromAPI } from './sessionDataTransform';
import { useDispatch, useSelector } from 'react-redux';
import { getSessionAttendeeDetails } from '../../redux/actions/session';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import CircularLoader from '../Loaders/CircularLoader';

// Extend dayjs with quarterOfYear plugin
dayjs.extend(quarterOfYear);

const COLORS = {
  'Completed': '#4CAF50',
  'Published': '#2196F3',
  'Draft': '#FF9800',
  'Yes': '#4CAF50',
  'No': '#F44336'
};

const SessionReporting = () => {
  // Data states
  const [sessionData, setSessionData] = useState([]);
  const [error, setError] = useState(null);

  // View state
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'chart', 'employee-analysis'
  const [employeeAnalysisTab, setEmployeeAnalysisTab] = useState('attendance'); // 'attendance' or 'no-shows'
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'quarterly', 'halfyearly'
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedHalf, setSelectedHalf] = useState('H1');
  const [customStartDate, setCustomStartDate] = useState(dayjs().subtract(1, 'year'));
  const [customEndDate, setCustomEndDate] = useState(dayjs());
  const [hostFilter, setHostFilter] = useState('');
  const [skillsFilter, setSkillsFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState('');
  
  // Table pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('sessionDate');
  const [order, setOrder] = useState('desc');
  
  // Export states
  const [exporting, setExporting] = useState(false);

  const dispatch = useDispatch();
  const { sessionAttendeeDetails } = useSelector(({ MODULES }) => MODULES);
  const { vTrackLoader } = useSelector(({ APP_STATE }) => APP_STATE);

  // Load via saga using base URL and shared http client
  useEffect(() => {
    dispatch(getSessionAttendeeDetails());
  }, [dispatch]);

  // Transform saga data to component shape
  useEffect(() => {
    // Process data when we receive it from the saga
    if (sessionAttendeeDetails && sessionAttendeeDetails.length >= 0) {
      try {
        const data = transformSessionDataFromAPI(sessionAttendeeDetails);
        setSessionData(data);
        setError(null);
      } catch (err) {
        setError('Failed to process session data');
        // eslint-disable-next-line no-console
        console.error('Error transforming session data:', err);
      }
    }
  }, [sessionAttendeeDetails]);

  // Get unique values for filter dropdowns
  const uniqueHosts = useMemo(() => getUniqueValues(sessionData, 'hosts'), [sessionData]);
  const uniqueSkills = useMemo(() => getUniqueValues(sessionData, 'targetedSkills'), [sessionData]);
  const uniqueStatuses = useMemo(() => getUniqueValues(sessionData, 'sessionStatus'), [sessionData]);

  // Filtered data based on current filters
  const filteredData = useMemo(() => {
    return sessionData.filter(session => {
      // Date filtering
      const sessionDate = dayjs(session.sessionDate);
      let dateMatches = true;
      
      if (dateFilter === 'quarterly') {
        const currentYear = dayjs().year();
        const quarterNum = parseInt(selectedQuarter.slice(1));
        const quarterStart = dayjs().year(currentYear).quarter(quarterNum).startOf('quarter');
        const quarterEnd = dayjs().year(currentYear).quarter(quarterNum).endOf('quarter');
        dateMatches = sessionDate.isBetween(quarterStart, quarterEnd, null, '[]');
      } else if (dateFilter === 'halfyearly') {
        const halfStart = selectedHalf === 'H1' ? dayjs().startOf('year') : dayjs().month(6).startOf('month');
        const halfEnd = selectedHalf === 'H1' ? dayjs().month(5).endOf('month') : dayjs().endOf('year');
        dateMatches = sessionDate.isBetween(halfStart, halfEnd, null, '[]');
      } else if (dateFilter === 'custom') {
        dateMatches = sessionDate.isBetween(customStartDate, customEndDate, null, '[]');
      }

      // Other filters
      const hostMatches = !hostFilter || session.hostNames.toLowerCase().includes(hostFilter.toLowerCase());
      const skillsMatches = !skillsFilter || session.targetedSkills.toLowerCase().includes(skillsFilter.toLowerCase());
      const statusMatches = !statusFilter || session.sessionStatus === statusFilter;
      const feedbackMatches = !feedbackFilter || session.feedbackSubmitted === feedbackFilter;

      return dateMatches && hostMatches && skillsMatches && statusMatches && feedbackMatches;
    });
  }, [sessionData, dateFilter, selectedQuarter, selectedHalf, customStartDate, customEndDate, hostFilter, skillsFilter, statusFilter, feedbackFilter]);

  // Chart data preparation
  const chartData = useMemo(() => {
    // Status distribution
    const statusCounts = filteredData.reduce((acc, session) => {
      acc[session.sessionStatus] = (acc[session.sessionStatus] || 0) + 1;
      return acc;
    }, {});

    const statusChartData = Object.keys(statusCounts).map(status => ({
      name: status,
      count: statusCounts[status],
      fill: COLORS[status] || '#8884d8'
    }));

    // Feedback distribution
    const feedbackCounts = filteredData.reduce((acc, session) => {
      acc[session.feedbackSubmitted] = (acc[session.feedbackSubmitted] || 0) + 1;
      return acc;
    }, {});

    const feedbackChartData = Object.keys(feedbackCounts).map(feedback => ({
      name: feedback,
      count: feedbackCounts[feedback],
      fill: COLORS[feedback] || '#8884d8'
    }));

    // Monthly trend data
    const monthlyData = filteredData.reduce((acc, session) => {
      const month = dayjs(session.sessionDate).format('MMM YYYY');
      if (!acc[month]) {
        acc[month] = {
          month,
          completed: 0,
          published: 0,
          draft: 0,
          total: 0
        };
      }
      acc[month][session.sessionStatus.toLowerCase()] += 1;
      acc[month].total += 1;
      return acc;
    }, {});

    const monthlyChartData = Object.values(monthlyData).sort((a, b) => 
      dayjs(a.month, 'MMM YYYY').valueOf() - dayjs(b.month, 'MMM YYYY').valueOf()
    );

    // Skills popularity
    const skillsCounts = filteredData.reduce((acc, session) => {
      session.targetedSkills.split(', ').forEach(skill => {
        const trimmedSkill = skill.trim();
        acc[trimmedSkill] = (acc[trimmedSkill] || 0) + 1;
      });
      return acc;
    }, {});

    const skillsChartData = Object.keys(skillsCounts)
      .map(skill => ({
        skill,
        count: skillsCounts[skill]
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10 skills

    return {
      statusChartData,
      feedbackChartData,
      monthlyChartData,
      skillsChartData
    };
  }, [filteredData]);

  // Grid columns configuration - optimized for space
  const gridColumns = [
    { id: 'sessionName', label: 'Session Details', sortable: true, width: '25%' },
    { id: 'sessionDate', label: 'Date', sortable: true, width: '8%' },
    { id: 'hostNames', label: 'Host(s)', sortable: true, width: '12%' },
    { id: 'targetedSkills', label: 'Skills', sortable: true, width: '15%' },
    { id: 'sessionStatus', label: 'Status', sortable: true, width: '8%' },
    { id: 'attendance', label: 'Attendance', sortable: true, width: '12%' }, // Combined column
    { id: 'feedback', label: 'Feedback', sortable: true, width: '10%' }, // Combined column
    { id: 'averageRating', label: 'Rating', sortable: true, width: '10%' }
  ];

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    
    // Map combined columns to actual sort properties
    let sortProperty = property;
    if (property === 'attendance') {
      sortProperty = 'attendanceRate';
    } else if (property === 'feedback') {
      sortProperty = 'feedbackSubmissionRate';
    }
    
    setOrderBy(sortProperty);
  };

  // Sort the filtered data
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      if (orderBy === 'sessionDate') {
        const dateA = new Date(a[orderBy]);
        const dateB = new Date(b[orderBy]);
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (typeof a[orderBy] === 'number') {
        return order === 'asc' ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
      }
      
      const valA = a[orderBy]?.toString().toLowerCase() || '';
      const valB = b[orderBy]?.toString().toLowerCase() || '';
      
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredData, order, orderBy]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setDateFilter('all');
    setHostFilter('');
    setSkillsFilter('');
    setStatusFilter('');
    setFeedbackFilter('');
    setCustomStartDate(dayjs().subtract(1, 'year'));
    setCustomEndDate(dayjs());
    setPage(0); // Reset pagination when filters change
  };

  // Employee analysis functions
  const getEmployeeAttendanceAnalysis = useMemo(() => {
    const employeeStats = {};
    
    filteredData.forEach(session => {
      session.attendees.forEach(attendee => {
        const key = `${attendee.employeeId}-${attendee.name}`;
        if (!employeeStats[key]) {
          employeeStats[key] = {
            employeeId: attendee.employeeId,
            name: attendee.name,
            email: attendee.email,
            totalRegistrations: 0,
            totalAttendance: 0,
            noShows: 0,
            sessions: []
          };
        }
        
        // Count as registration (every attendee record means they registered for that session)
        employeeStats[key].totalRegistrations++;
        
        employeeStats[key].sessions.push({
          sessionName: session.sessionName,
          sessionDate: session.sessionDate,
          attendanceStatus: attendee.attendanceStatus,
          feedbackSubmitted: attendee.feedbackSubmitted
        });
        
        // Only count as attendance if status is 'Present'
        if (attendee.attendanceStatus === 'Present') {
          employeeStats[key].totalAttendance++;
        } 
        // Count as no-show if they registered but didn't attend (NULL, Absent, or any other status except Present)
        else if (attendee.attendanceStatus === 'NULL' || attendee.attendanceStatus === 'Absent' || attendee.attendanceStatus === null || attendee.attendanceStatus === '') {
          employeeStats[key].noShows++;
        }
      });
    });

    // Convert to array and calculate rates
    return Object.values(employeeStats).map(emp => {
      return {
        ...emp,
        attendanceRate: emp.totalRegistrations > 0 ? 
          ((emp.totalAttendance / emp.totalRegistrations) * 100).toFixed(1) : '0',
        noShowRate: emp.totalRegistrations > 0 ? 
          ((emp.noShows / emp.totalRegistrations) * 100).toFixed(1) : '0'
      };
    });
  }, [filteredData]);

  // Get top attendees (employees with most training attendance)
  const getTopAttendees = useMemo(() => {
    return getEmployeeAttendanceAnalysis
      .filter(emp => emp.totalAttendance > 0)
      .sort((a, b) => b.totalAttendance - a.totalAttendance)
      .slice(0, 20); // Top 20 attendees
  }, [getEmployeeAttendanceAnalysis]);

  // Get employees with no-shows (registered but didn't attend)
  const getNoShowEmployees = useMemo(() => {
    return getEmployeeAttendanceAnalysis
      .filter(emp => emp.noShows > 0)
      .sort((a, b) => b.noShowRate - a.noShowRate);
  }, [getEmployeeAttendanceAnalysis]);

  // Export employee analysis to Excel
  const exportEmployeeAnalysis = async () => {
    setExporting(true);
    
    try {
      const wb = XLSX.utils.book_new();
      
      // Top Attendees Sheet
      const topAttendeesData = getTopAttendees.map((emp, index) => ({
        'Rank': index + 1,
        'Employee Name': emp.name,
        'Employee ID': emp.employeeId,
        'Email': emp.email,
        'Total Registrations': emp.totalRegistrations,
        'Sessions Attended': emp.totalAttendance,
        'Attendance Rate (%)': emp.attendanceRate,
        'No-Shows': emp.noShows,
        'No-Show Rate (%)': emp.noShowRate
      }));
      
      const attendeesWs = XLSX.utils.json_to_sheet(topAttendeesData);
      attendeesWs['!cols'] = [
        { wch: 6 }, { wch: 25 }, { wch: 12 }, { wch: 30 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, attendeesWs, 'Top Attendees');
      
      // No-Show Analysis Sheet
      const noShowData = getNoShowEmployees.map(emp => {
        const riskLevel = parseFloat(emp.noShowRate) >= 50 ? 'High' :
                         parseFloat(emp.noShowRate) >= 25 ? 'Medium' : 'Low';
        return {
          'Employee Name': emp.name,
          'Employee ID': emp.employeeId,
          'Email': emp.email,
          'Total Registrations': emp.totalRegistrations,
          'Sessions Attended': emp.totalAttendance,
          'No-Shows': emp.noShows,
          'No-Show Rate (%)': emp.noShowRate,
          'Risk Level': riskLevel
        };
      });
      
      const noShowWs = XLSX.utils.json_to_sheet(noShowData);
      noShowWs['!cols'] = [
        { wch: 25 }, { wch: 12 }, { wch: 30 }, { wch: 15 },
        { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, noShowWs, 'No-Show Analysis');
      
      // Summary Sheet
      const summaryData = [
        ['Employee Training Analysis Report', ''],
        ['Generated On', dayjs().format('DD MMM YYYY, HH:mm')],
        ['', ''],
        ['Total Unique Employees', getEmployeeAttendanceAnalysis.length],
        ['Active Attendees', getTopAttendees.length],
        ['Employees with No-Shows', getNoShowEmployees.length],
        ['', ''],
        ['Applied Filters', ''],
        ['Date Filter', dateFilter === 'all' ? 'All Time' : dateFilter],
        ['Host Filter', hostFilter || 'None'],
        ['Skills Filter', skillsFilter || 'None'],
        ['Status Filter', statusFilter || 'None'],
        ['Feedback Filter', feedbackFilter || 'None']
      ];
      
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
      
      // Generate filename
      const today = dayjs().format('YYYY-MM-DD');
      const filename = `Employee_Training_Analysis_${today}.xlsx`;
      
      // Save the file
      XLSX.writeFile(wb, filename);
      
      toast.success(`Employee analysis exported successfully! ${getEmployeeAttendanceAnalysis.length} employees analyzed.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
    } catch (error) {
      console.error('Error exporting employee analysis:', error);
      toast.error('Failed to export employee analysis. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel function
  const exportToExcel = async () => {
    setExporting(true);
    
    try {
      // Prepare data for Excel export
      const exportData = filteredData.map(session => ({
        'Session Name': session.sessionName,
        'Date': dayjs(session.sessionDate).format('DD MMM YYYY'),
        'Host(s)': session.hostNames,
        'Targeted Skills': session.targetedSkills,
        'Status': session.sessionStatus,
        'Feedback Submitted': session.feedbackSubmitted,
        'Total Seats': session.totalSeats,
        'Registered Participants': session.registeredParticipants,
        'Actual Attendees': session.actualAttendees,
        'Attendance Rate (%)': session.attendanceRate,
        'Feedback Submission Rate (%)': session.feedbackSubmissionRate,
        'Average Rating': session.averageRating && parseFloat(session.averageRating) > 0 ? parseFloat(session.averageRating).toFixed(1) : 'No ratings',
        'Rating Count': session.ratingCount || 0,
        'Session Description': session.sessionDescription
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 30 }, // Session Name
        { wch: 12 }, // Date
        { wch: 20 }, // Host(s)
        { wch: 25 }, // Targeted Skills
        { wch: 12 }, // Status
        { wch: 15 }, // Feedback Submitted
        { wch: 12 }, // Total Seats
        { wch: 15 }, // Registered Participants
        { wch: 15 }, // Actual Attendees
        { wch: 15 }, // Attendance Rate
        { wch: 20 }, // Feedback Submission Rate
        { wch: 15 }, // Average Rating
        { wch: 12 }, // Rating Count
        { wch: 40 }  // Session Description
      ];
      ws['!cols'] = colWidths;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Session Report');

      // Create summary sheet
      const summaryData = [
        ['Session Report Summary', ''],
        ['Generated On', dayjs().format('DD MMM YYYY, HH:mm')],
        ['', ''],
        ['Total Sessions', filteredData.length],
        ['Completed Sessions', filteredData.filter(s => s.sessionStatus === 'Completed').length],
        ['Published Sessions', filteredData.filter(s => s.sessionStatus === 'Published').length],
        ['Draft Sessions', filteredData.filter(s => s.sessionStatus === 'Draft').length],
        ['', ''],
        ['Total Registered', filteredData.reduce((sum, s) => sum + s.registeredParticipants, 0)],
        ['Total Attendees', filteredData.reduce((sum, s) => sum + s.actualAttendees, 0)],
        ['Average Attendance Rate', filteredData.length > 0 ? ((filteredData.reduce((sum, s) => sum + parseFloat(s.attendanceRate), 0) / filteredData.length).toFixed(1) + '%') : '0%'],
        ['', ''],
        ['Total Feedback Submitted', filteredData.reduce((sum, s) => sum + s.feedbackSubmittedCount, 0)],
        ['Average Feedback Rate', filteredData.length > 0 ? ((filteredData.reduce((sum, s) => sum + parseFloat(s.feedbackSubmissionRate), 0) / filteredData.length).toFixed(1) + '%') : '0%'],
        ['Unique Hosts', new Set(filteredData.flatMap(s => s.hosts)).size],
        ['', ''],
        ['Applied Filters', ''],
        ['Date Filter', dateFilter === 'all' ? 'All Time' : dateFilter],
        ['Host Filter', hostFilter || 'None'],
        ['Skills Filter', skillsFilter || 'None'],
        ['Status Filter', statusFilter || 'None'],
        ['Feedback Filter', feedbackFilter || 'None']
      ];

      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Style the summary sheet
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 20 }];
      
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

      // Generate filename with current date and applied filters
      const today = dayjs().format('YYYY-MM-DD');
      let filename = `Session_Report_${today}`;
      
      // Add filter info to filename if filters are applied
      const activeFilters = [];
      if (dateFilter !== 'all') activeFilters.push(dateFilter);
      if (hostFilter) activeFilters.push('host-filtered');
      if (skillsFilter) activeFilters.push('skills-filtered');
      if (statusFilter) activeFilters.push(statusFilter);
      if (feedbackFilter) activeFilters.push(feedbackFilter);
      
      if (activeFilters.length > 0) {
        filename += `_${activeFilters.join('_')}`;
      }
      
      filename += '.xlsx';

      // Save the file
      XLSX.writeFile(wb, filename);

      // Show success message
      toast.success(`Excel file exported successfully! ${filteredData.length} sessions exported.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="session-reporting-container">
        <Box className="session-reporting-header">
          <Typography variant="h4" component="h1" className="page-title">
            Session Reporting Dashboard
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
          <Typography variant="h6" color="error" gutterBottom>
            Error loading session data
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {error}
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="session-reporting-container">
      {/* Header */}
      <Box className="session-reporting-header">
        <Typography variant="h4" component="h1" className="page-title">
          Session Reporting Dashboard
        </Typography>
        
        {/* View Toggle */}
        <Box className="view-controls">
          <FormControl size="small" className="view-selector">
            <InputLabel>View Mode</InputLabel>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              label="View Mode"
            >
              <MenuItem value="grid">📊 Session Grid</MenuItem>
              <MenuItem value="chart">📈 Charts & Analytics</MenuItem>
              <MenuItem value="employee-analysis">👥 Employee Analysis</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Filters Section */}
      <Paper className="filters-section" elevation={2}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        
        <Grid container spacing={2} alignItems="center">
          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date Filter"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="halfyearly">Half Yearly</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Quarterly Selection */}
          {dateFilter === 'quarterly' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Quarter</InputLabel>
                <Select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  label="Quarter"
                >
                  <MenuItem value="Q1">Q1 (Jan-Mar)</MenuItem>
                  <MenuItem value="Q2">Q2 (Apr-Jun)</MenuItem>
                  <MenuItem value="Q3">Q3 (Jul-Sep)</MenuItem>
                  <MenuItem value="Q4">Q4 (Oct-Dec)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Half Yearly Selection */}
          {dateFilter === 'halfyearly' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Half Year</InputLabel>
                <Select
                  value={selectedHalf}
                  onChange={(e) => setSelectedHalf(e.target.value)}
                  label="Half Year"
                >
                  <MenuItem value="H1">H1 (Jan-Jun)</MenuItem>
                  <MenuItem value="H2">H2 (Jul-Dec)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={customStartDate ? customStartDate.format('YYYY-MM-DD') : ''}
                  onChange={(e) => setCustomStartDate(dayjs(e.target.value))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={customEndDate ? customEndDate.format('YYYY-MM-DD') : ''}
                  onChange={(e) => setCustomEndDate(dayjs(e.target.value))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </>
          )}

          {/* Host Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Host</InputLabel>
              <Select
                value={hostFilter}
                onChange={(e) => setHostFilter(e.target.value)}
                label="Host"
              >
                <MenuItem value="">All Hosts</MenuItem>
                {uniqueHosts.map(host => (
                  <MenuItem key={host} value={host}>{host}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Skills Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Targeted Skills"
              value={skillsFilter}
              onChange={(e) => setSkillsFilter(e.target.value)}
              placeholder="Search skills..."
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Session Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Session Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {uniqueStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Feedback Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Feedback Submitted</InputLabel>
              <Select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value)}
                label="Feedback Submitted"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters Button */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              size="small"
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Stats - Compact 2-Row Layout */}
      <Box className="kpi-section">
        <Typography variant="h6" className="kpi-section-title" gutterBottom>
          📈 Key Performance Indicators
        </Typography>
      </Box>
      <Grid container spacing={1.5} className="summary-stats-compact">
        {/* First Row - 4 KPIs */}
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact sessions-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">{filteredData.length}</Typography>
              <Typography variant="caption">Sessions</Typography>
            </div>
            <span className="stat-card-icon">📊</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact attendance-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.reduce((sum, s) => sum + s.actualAttendees, 0)}
              </Typography>
              <Typography variant="caption">Attendees</Typography>
            </div>
            <span className="stat-card-icon">👥</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact rate-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.length > 0 
                  ? ((filteredData.reduce((sum, s) => sum + parseFloat(s.attendanceRate), 0) / filteredData.length).toFixed(1))
                  : 0
                }%
              </Typography>
              <Typography variant="caption">Attendance</Typography>
            </div>
            <span className="stat-card-icon">📈</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact feedback-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.length > 0 
                  ? ((filteredData.reduce((sum, s) => sum + parseFloat(s.feedbackSubmissionRate), 0) / filteredData.length).toFixed(1))
                  : 0
                }%
              </Typography>
              <Typography variant="caption">Feedback</Typography>
            </div>
            <span className="stat-card-icon">💬</span>
          </Paper>
        </Grid>
        
        {/* Second Row - 4 KPIs */}
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact rating-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.length > 0 
                  ? (() => {
                      const sessionsWithRatings = filteredData.filter(s => s.averageRating !== null && s.averageRating !== undefined && parseFloat(s.averageRating) > 0);
                      return sessionsWithRatings.length > 0 
                        ? (sessionsWithRatings.reduce((sum, s) => sum + parseFloat(s.averageRating), 0) / sessionsWithRatings.length).toFixed(1)
                        : 'N/A';
                    })()
                  : 'N/A'
                }
                {filteredData.length > 0 && filteredData.some(s => s.averageRating !== null && s.averageRating !== undefined && parseFloat(s.averageRating) > 0) && (
                  <span style={{ fontSize: '0.7em', color: '#FFB400', marginLeft: '2px' }}>⭐</span>
                )}
              </Typography>
              <Typography variant="caption">Rating</Typography>
            </div>
            <span className="stat-card-icon">⭐</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact completed-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.filter(s => s.sessionStatus === 'Completed').length}
              </Typography>
              <Typography variant="caption">Completed</Typography>
            </div>
            <span className="stat-card-icon">✅</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact hosts-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {new Set(filteredData.flatMap(s => s.hosts)).size}
              </Typography>
              <Typography variant="caption">Hosts</Typography>
            </div>
            <span className="stat-card-icon">🎯</span>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper className="stat-card stat-card-compact feedback-submitted-card" elevation={0}>
            <div className="stat-card-content">
              <Typography variant="h5">
                {filteredData.reduce((sum, s) => sum + s.feedbackSubmittedCount, 0)}
              </Typography>
              <Typography variant="caption">Feedbacks</Typography>
            </div>
            <span className="stat-card-icon">📝</span>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content */}
      <div className="main-content-session">
        {viewMode === 'employee-analysis' ? (
          <div className="employee-analysis-container">
            {/* Employee Analysis Header */}
            <Paper className="analysis-header" elevation={2}>
              <Typography variant="h5" className="analysis-title" gutterBottom>
                👥 Employee Training Analysis
              </Typography>
              <Typography variant="body1" className="analysis-subtitle" gutterBottom>
                Analyze employee participation patterns and training engagement
              </Typography>
              
              {/* Sub-tabs for Employee Analysis */}
              <Box className="analysis-tabs">
                <Button
                  variant={employeeAnalysisTab === 'attendance' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeAnalysisTab('attendance')}
                  className="analysis-tab-button"
                  startIcon={<span>🏆</span>}
                >
                  Top Attendees
                </Button>
                <Button
                  variant={employeeAnalysisTab === 'no-shows' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeAnalysisTab('no-shows')}
                  className="analysis-tab-button"
                  startIcon={<span>⚠️</span>}
                >
                  No-Show Analysis
                </Button>
              </Box>
            </Paper>

            {/* Employee Analysis Content */}
            {employeeAnalysisTab === 'attendance' ? (
              <Paper className="analysis-content">
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                  <div>
                    <Typography variant="h6" gutterBottom>
                      🏆 Top Training Attendees ({getTopAttendees.length} employees)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Employees who are actively participating in training sessions
                    </Typography>
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={exportEmployeeAnalysis}
                    disabled={getEmployeeAttendanceAnalysis.length === 0 || exporting}
                    startIcon={exporting ? <span>⏳</span> : <span>📥</span>}
                    className="export-button"
                  >
                    {exporting ? 'Exporting...' : 'Export Analysis'}
                  </Button>
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Rank</strong></TableCell>
                        <TableCell><strong>Employee Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell align="center"><strong>Registered Sessions</strong></TableCell>
                        <TableCell align="center"><strong>Attended (Present)</strong></TableCell>
                        <TableCell align="center"><strong>Attendance Rate %</strong></TableCell>
                        <TableCell align="center"><strong>No-Shows/Absent</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getTopAttendees.map((employee, index) => (
                        <TableRow key={employee.employeeId} hover>
                          <TableCell>
                            <Chip 
                              label={`#${index + 1}`} 
                              color={index < 3 ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <strong>{employee.name}</strong>
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                ID: {employee.employeeId}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{employee.email}</TableCell>
                          <TableCell align="center">
                            <Chip label={employee.totalRegistrations} variant="outlined" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={employee.totalAttendance} 
                              color="success" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              style={{ 
                                color: parseFloat(employee.attendanceRate) >= 80 ? '#4CAF50' : 
                                       parseFloat(employee.attendanceRate) >= 60 ? '#FF9800' : '#F44336',
                                fontWeight: 'bold'
                              }}
                            >
                              {employee.attendanceRate}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={employee.noShows} 
                              color={employee.noShows > 0 ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Paper className="analysis-content">
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                  <div>
                    <Typography variant="h6" gutterBottom>
                      ⚠️ No-Show Analysis ({getNoShowEmployees.length} employees)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Employees who registered for sessions but didn't attend
                    </Typography>
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={exportEmployeeAnalysis}
                    disabled={getEmployeeAttendanceAnalysis.length === 0 || exporting}
                    startIcon={exporting ? <span>⏳</span> : <span>📥</span>}
                    className="export-button"
                  >
                    {exporting ? 'Exporting...' : 'Export Analysis'}
                  </Button>
                </Box>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Employee Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell align="center"><strong>Registered Sessions</strong></TableCell>
                        <TableCell align="center"><strong>Attended (Present)</strong></TableCell>
                        <TableCell align="center"><strong>No-Shows/Absent</strong></TableCell>
                        <TableCell align="center"><strong>No-Show Rate %</strong></TableCell>
                        <TableCell align="center"><strong>Risk Level</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getNoShowEmployees.map((employee) => {
                        const riskLevel = parseFloat(employee.noShowRate) >= 50 ? 'High' :
                                         parseFloat(employee.noShowRate) >= 25 ? 'Medium' : 'Low';
                        const riskColor = riskLevel === 'High' ? 'error' :
                                         riskLevel === 'Medium' ? 'warning' : 'success';
                        
                        return (
                          <TableRow key={employee.employeeId} hover>
                            <TableCell>
                              <div>
                                <strong>{employee.name}</strong>
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  ID: {employee.employeeId}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{employee.email}</TableCell>
                            <TableCell align="center">
                              <Chip label={employee.totalRegistrations} variant="outlined" size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={employee.totalAttendance} 
                                color="success" 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={employee.noShows} 
                                color="error" 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="body2" 
                                style={{ 
                                  color: parseFloat(employee.noShowRate) >= 50 ? '#F44336' : 
                                         parseFloat(employee.noShowRate) >= 25 ? '#FF9800' : '#4CAF50',
                                  fontWeight: 'bold'
                                }}
                              >
                                {employee.noShowRate}%
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={riskLevel} 
                                color={riskColor}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <Paper className="grid-container">
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
              <Typography variant="h4" className="table-title">
                Session Details ({filteredData.length} sessions)
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={exportToExcel}
                disabled={filteredData.length === 0 || exporting}
                startIcon={exporting ? <span>⏳</span> : <span>📥</span>}
                className="export-button"
              >
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Box>
            <TableContainer sx={{ 
              maxWidth: '100%', 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px',
              }
            }}>
              <Table sx={{ 
                tableLayout: 'fixed', 
                width: '100%', 
                minWidth: '900px' // Minimum width to prevent squishing
              }}>
                <TableHead>
                  <TableRow>
                    {gridColumns.map((column) => (
                      <TableCell 
                        key={column.id}
                        sx={{ 
                          width: column.width,
                          padding: '8px 12px',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow key={row.id} hover>
                      {/* Session Details - Combined name and description */}
                      <TableCell sx={{ padding: '8px 12px' }}>
                        <div style={{ overflow: 'hidden' }}>
                          <strong style={{ fontSize: '0.875rem', display: 'block', marginBottom: '2px' }}>
                            {row.sessionName.length > 40 ? `${row.sessionName.substring(0, 40)}...` : row.sessionName}
                          </strong>
                          {row.sessionDescription && (
                            <div style={{ fontSize: '0.7rem', color: '#666', lineHeight: '1.2' }}>
                              {row.sessionDescription.length > 80 
                                ? `${row.sessionDescription.substring(0, 80)}...` 
                                : row.sessionDescription
                              }
                            </div>
                          )}
                        </div>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                        {dayjs(row.sessionDate).format('MMM DD')}
                      </TableCell>

                      {/* Host(s) */}
                      <TableCell sx={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.hostNames.length > 20 ? `${row.hostNames.substring(0, 20)}...` : row.hostNames}
                        </div>
                      </TableCell>

                      {/* Skills */}
                      <TableCell sx={{ padding: '8px 12px' }}>
                        <Chip 
                          label={row.targetedSkills.length > 25 ? `${row.targetedSkills.substring(0, 25)}...` : row.targetedSkills}
                          variant="outlined"
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '24px' }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ padding: '8px 12px' }}>
                        <Chip 
                          label={row.sessionStatus} 
                          color={row.sessionStatus === 'Completed' ? 'success' : row.sessionStatus === 'Published' ? 'primary' : 'warning'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '24px' }}
                        />
                      </TableCell>

                      {/* Attendance - Combined metrics */}
                      <TableCell sx={{ padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.8rem' }}>
                          <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {row.actualAttendees}/{row.registeredParticipants}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>
                            {row.attendanceRate}%
                          </div>
                        </div>
                      </TableCell>

                      {/* Feedback - Combined status and rate */}
                      <TableCell sx={{ padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.8rem' }}>
                          <Chip 
                            label={row.feedbackSubmitted} 
                            color={row.feedbackSubmitted === 'Yes' ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: '20px', marginBottom: '2px' }}
                          />
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>
                            {row.feedbackSubmissionRate}%
                          </div>
                        </div>
                      </TableCell>

                      {/* Average Rating */}
                      <TableCell sx={{ padding: '8px 12px', textAlign: 'center' }}>
                        {row.averageRating && parseFloat(row.averageRating) > 0 ? (
                          <div style={{ fontSize: '0.8rem' }}>
                            <div style={{ fontWeight: 'bold', color: '#1773BC', marginBottom: '2px' }}>
                              {parseFloat(row.averageRating).toFixed(1)} ⭐
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>
                              ({row.ratingCount || 0})
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.75rem' }}>No ratings</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        ) : (
          <div className="charts-container">
            {/* Session Status Distribution */}
            <Paper className="chart-paper">
              <Typography variant="h6" gutterBottom>Session Status Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count, percent }) => `${name}: ${count} (${(percent * 100).toFixed(1)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {chartData.statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Feedback Submission Distribution */}
            <Paper className="chart-paper">
              <Typography variant="h6" gutterBottom>Feedback Submission Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.feedbackChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count, percent }) => `${name}: ${count} (${(percent * 100).toFixed(1)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {chartData.feedbackChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Monthly Session Trends */}
            <Paper className="chart-paper full-width">
              <Typography variant="h6" gutterBottom>Monthly Session Trends</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData.monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="completed" 
                    stroke="#4CAF50" 
                    strokeWidth={2}
                    name="Completed"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="published" 
                    stroke="#2196F3" 
                    strokeWidth={2}
                    name="Published"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="draft" 
                    stroke="#FF9800" 
                    strokeWidth={2}
                    name="Draft"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            {/* Top Skills */}
            <Paper className="chart-paper full-width">
              <Typography variant="h6" gutterBottom>Most Popular Skills</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData.skillsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="skill" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1773BC" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionReporting;
